import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.scss';
import { CircularProgress, Grid, Box, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { getTodayISO } from '../../shared/utils/dateUtils';
import { getDays } from '../../core/API/organizationSettings';
import { getCategorizedToDos, getToDoPrioritys } from '../../core/API/todo';
import { getCategorizedChecklistScheduleEvents } from '../../core/API/checklist';
import { getBroadcastMessage } from '../../core/API/broadcast';
import { usePermissionCheck } from '../../core/hooks/usePermissionCheck';
import { useUserAssignmentGroupIds } from '../../core/hooks/useUserAssignmentGroupIds';
import { useWorkplaceUser } from '../../module/documents/utils';
import { useUserData } from '../../store/useUserData';
import { isChecklistEventAssignedToCurrentUser } from '../../core/utils/checklistEventAssignment';
import { filterTodosForMyScope } from '../../core/utils/todoAssignment';
import {
	buildPriorityLookup,
	normalizeAndSortTodos,
	normalizePrioritiesList,
	type TodoTableRow,
} from '../../core/utils/todoScheduleEvent';
import {
	SecurityRolePermission,
} from '../../tests/services/permissionService';
import { filterBroadcastInboxForUser } from '../../core/utils/broadcastInbox';
import {
	getCurrentUserId,
	resolveEmployeeWidgetVisibility,
} from './utils/homePage';
import {
	fetchMyPublishedShifts,
	resolveStartOfWeekLabel,
} from './utils/scheduleWidget';
import {
	filterBroadcastsForToday,
	filterTodosForToday,
	getBroadcastMessageDate,
} from './utils/homePageDateFilters';
import EmployeeChecklistWidget from './widgets/EmployeeChecklistWidget';
import EmployeeTodoWidget from './widgets/EmployeeTodoWidget';
import EmployeeBroadcastWidget from './widgets/EmployeeBroadcastWidget';
import EmployeeScheduleWidget from './widgets/EmployeeScheduleWidget';
import './widgets/EmployeeHomeWidgets.scss';

interface ChecklistScheduleEvent {
	id: number;
	checklistTemplate: { label: string; type?: string };
	checklistSchedule?: {
		assignmentType: string;
		assignee?: { id: number; firstName: string; lastName: string } | null;
		group?: { id: number; label: string } | null;
	};
	scheduledDate: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	scheduledTime?: string;
}

const getPriorityOrder = (priorityLabel?: string | null): number => {
	const label = (priorityLabel || '').toLowerCase();
	if (label === 'critical' || label === 'urgent') return 1;
	if (label === 'high') return 2;
	if (label === 'medium') return 3;
	if (label === 'low') return 4;
	return 5;
};

const sortBroadcastsByPriority = <T extends Record<string, unknown>>(messages: T[]): T[] =>
	[...messages].sort((a, b) => {
		const priorityA = a.priority as { label?: string; name?: string } | undefined;
		const priorityB = b.priority as { label?: string; name?: string } | undefined;
		const orderA = getPriorityOrder(priorityA?.label || priorityA?.name);
		const orderB = getPriorityOrder(priorityB?.label || priorityB?.name);
		if (orderA !== orderB) return orderA - orderB;

		const dateA = getBroadcastMessageDate(a);
		const dateB = getBroadcastMessageDate(b);
		if (dateA && dateB) return dateB.getTime() - dateA.getTime();
		return 0;
	});

const EmployeeHomePage = () => {
	const { hasModuleViewPermission } = usePermissionCheck();
	const { userOrg } = useWorkplaceUser();
	const { currentUserRole } = useUserData();
	const products = useMemo(() => userOrg?.products || [], [userOrg?.products]);
	const canViewHomePage = hasModuleViewPermission('Dashboard');
	const workplaceId = Number(localStorage.getItem('workplace-id') || 1);
	const currentUserId = getCurrentUserId();
	const currentSecurityRoleId = currentUserRole?.id ?? null;
	const { data: daysData } = useQuery('days', () => getDays(), {
		staleTime: 300000,
	});
	const startOfWeekLabel = resolveStartOfWeekLabel(userOrg, daysData);

	const rolePermissions = useMemo(
		() => (currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
		[currentUserRole],
	);

	const widgets = useMemo(
		() => resolveEmployeeWidgetVisibility(rolePermissions, products),
		[rolePermissions, products],
	);

	const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
	const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
	const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');

	const needsAssignmentGroups = widgets.canViewChecklist || widgets.canViewTodos;

	const { userGroupIds, isLoading: groupsLoading } = useUserAssignmentGroupIds(
		workplaceId,
		needsAssignmentGroups,
	);

	const [loading, setLoading] = useState(true);
	const [myShifts, setMyShifts] = useState<Awaited<ReturnType<typeof fetchMyPublishedShifts>>>([]);
	const [myBroadcasts, setMyBroadcasts] = useState<Record<string, unknown>[]>([]);
	const [myChecklists, setMyChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [myTodos, setMyTodos] = useState<TodoTableRow[]>([]);

	useEffect(() => {
		if (needsAssignmentGroups && groupsLoading) {
			return;
		}

		const fetchData = async () => {
			try {
				const today = getTodayISO();
				const tasks: Promise<void>[] = [];

				if (widgets.canViewSchedule) {
					tasks.push(
						(async () => {
							const shifts = await fetchMyPublishedShifts(workplaceId, startOfWeekLabel);
							setMyShifts(shifts);
						})(),
					);
				}

				if (widgets.canViewBroadcasts) {
					tasks.push(
						(async () => {
							const response = await getBroadcastMessage({
								workplaceId,
								page: 1,
								itemsPerPage: 100,
							});
							const inbox = Array.isArray(response?.member)
								? response.member
								: Array.isArray(response)
									? response
									: [];
							const scopedInbox = filterBroadcastInboxForUser(inbox, {
								currentUserId,
								canViewBroadcastMy,
								canViewBroadcastAll,
								canViewBroadcastOrganization,
							}) as Record<string, unknown>[];
							const unreadToday = sortBroadcastsByPriority(
								filterBroadcastsForToday(
									scopedInbox.filter((message) => message.isRead === false),
								),
							);
							setMyBroadcasts(unreadToday);
						})(),
					);
				}

				if (widgets.canViewChecklist) {
					tasks.push(
						(async () => {
							const checklistData = await getCategorizedChecklistScheduleEvents(
								today,
								workplaceId,
							);
							const items = checklistData?.myOutstanding ?? [];
							// Match Checklist module "My Outstanding" — always scoped to current user/groups.
							const scoped = items.filter((item) =>
								isChecklistEventAssignedToCurrentUser(
									item,
									currentUserId,
									userGroupIds,
									currentSecurityRoleId,
								),
							);
							setMyChecklists(scoped);
						})(),
					);
				}

				if (widgets.canViewTodos) {
					tasks.push(
						(async () => {
							const [todosResponse, prioritiesResponse] = await Promise.all([
								getCategorizedToDos({
									workplaceId,
									page: 1,
									itemsPerPage: 100,
								}),
								getToDoPrioritys(),
							]);
							const myTodosData = Array.isArray(todosResponse?.my_todos)
								? todosResponse.my_todos
								: [];
							const priorityLookup = buildPriorityLookup(
								normalizePrioritiesList(prioritiesResponse),
							);
							const scopedTodos = filterTodosForMyScope(
								myTodosData,
								currentUserId,
								userGroupIds,
								currentSecurityRoleId,
							) as TodoTableRow[];
							const mergedTodos = normalizeAndSortTodos(scopedTodos, priorityLookup);
							setMyTodos(filterTodosForToday(mergedTodos));
						})(),
					);
				}

				await Promise.all(tasks);
			} catch (error) {
				console.error('Error fetching employee home data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [
		workplaceId,
		widgets,
		rolePermissions,
		currentUserId,
		currentUserRole,
		currentSecurityRoleId,
		userGroupIds,
		startOfWeekLabel,
		needsAssignmentGroups,
		groupsLoading,
		canViewBroadcastMy,
		canViewBroadcastAll,
		canViewBroadcastOrganization,
	]);

	const visibleWidgetCount = [
		widgets.canViewSchedule,
		widgets.canViewBroadcasts,
		widgets.canViewChecklist,
		widgets.canViewTodos,
	].filter(Boolean).length;

	if (!canViewHomePage) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '400px',
					p: 3,
				}}
			>
				<Typography variant="h6" color="textSecondary">
					You do not have permission to view Home.
				</Typography>
			</Box>
		);
	}

	if (loading && visibleWidgetCount === 0) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<div className="dashboard-container">
			<Grid container spacing={2} className="employee-home-grid">
				{widgets.canViewSchedule && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 1 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeScheduleWidget loading={loading} shifts={myShifts} />
					</Grid>
				)}

				{widgets.canViewBroadcasts && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 1 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeBroadcastWidget
							loading={loading}
							items={myBroadcasts as never[]}
							workplaceId={workplaceId}
						/>
					</Grid>
				)}

				{widgets.canViewChecklist && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 2 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeChecklistWidget loading={loading} items={myChecklists} />
					</Grid>
				)}

				{widgets.canViewTodos && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 2 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeTodoWidget loading={loading} items={myTodos} />
					</Grid>
				)}
			</Grid>

			{!loading && visibleWidgetCount === 0 && (
				<Box sx={{ textAlign: 'center', py: 6 }}>
					<Typography variant="h6" color="textSecondary">
						No licensed modules are available for Home.
					</Typography>
				</Box>
			)}
		</div>
	);
};

export default EmployeeHomePage;
